import PageHeader from '@/components/base/PageHeader';
import SectionNav from './components/SectionNav';
import StatOverview from './components/StatOverview';
import PerformanceCharts from './components/PerformanceCharts';
import MonthlyBreakdown from './components/MonthlyBreakdown';
import AccountTrust from './components/AccountTrust';
import RiskDisclosure from './components/RiskDisclosure';
import useCanonical from '@/hooks/useCanonical';

const CANONICAL_URL = '/performance';
const PAGE_TITLE = 'Live XAUUSD EA Performance — Real MT5 Gold Account Results';
const PAGE_DESCRIPTION =
  'Live performance of the free GoldWarden XAUUSD EA: equity growth, win rate, profit factor, drawdown and a full closed-trade log from a live MT5 gold account.';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
    { '@type': 'ListItem', position: 2, name: 'Performance', item: CANONICAL_URL },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${CANONICAL_URL}#webpage`,
  name: 'Real trading performance on a live MT5 gold account',
  description: PAGE_DESCRIPTION,
  url: CANONICAL_URL,
  inLanguage: 'en',
  isPartOf: { '@id': '/#website' },
  publisher: { '@id': '/#organization' },
};

export default function Performance() {
  useCanonical(CANONICAL_URL);

  return (
    <div>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <meta
        name="keywords"
        content="XAUUSD EA performance, MT5 gold EA results, live expert advisor track record, free gold trading bot, forex EA drawdown"
      />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="GoldWarden EA" />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta
        property="og:image"
        content="https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20centered%2C%20faint%20golden%20candlestick%20chart%20lines%20in%20the%20background%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20luxurious%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-goldwarden-01&orientation=landscape&nocache=true"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="Live GoldWarden XAUUSD MT5 account equity curve and key trading statistics"
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      <meta
        name="twitter:image"
        content="https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20centered%2C%20faint%20golden%20candlestick%20chart%20lines%20in%20the%20background%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20luxurious%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-goldwarden-01&orientation=landscape&nocache=true"
      />
      <meta
        name="twitter:image:alt"
        content="Live GoldWarden XAUUSD MT5 account equity curve and key trading statistics"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbSchema, webPageSchema]) }}
      />

      <PageHeader
        breadcrumb="Performance"
        eyebrow="Live track record"
        title="Real trading performance on a live MT5 gold account"
        subtitle="Everything on this page is pulled from a live account that trades only XAUUSD with GoldWarden EA — published in full, including the losing months."
      />

      <SectionNav />
      <StatOverview />
      <PerformanceCharts />
      <MonthlyBreakdown />
      <AccountTrust />
      <RiskDisclosure />
    </div>
  );
}